import { useState } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import { AdminReferral, AdminTableColumn, AdminTableAction } from '../../../types/admin';
import AdminTable from '../../admin/shared/AdminTable';
import AdminConfirmModal from '../../admin/shared/AdminConfirmModal';
import { format } from 'date-fns';

interface ReferralsModerationProps {
  referrals: AdminReferral[];
  isLoading: boolean;
  onSearch: (query: string) => void;
  onFilterStatus: (status: string) => void;
  onApprove: (referralId: string) => Promise<{ success: boolean; error?: string }>;
  onReject: (referralId: string, reason: string) => Promise<{ success: boolean; error?: string }>;
}

/**
 * ReferralsModeration - Referrals management tab
 *
 * Features:
 * - Search and filter referrals
 * - Approve referrals
 * - Reject referrals with reason
 * - View approval/rejection details
 */
export default function ReferralsModeration({
  referrals,
  isLoading,
  onSearch,
  onFilterStatus,
  onApprove,
  onReject,
}: ReferralsModerationProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [referralToApprove, setReferralToApprove] = useState<AdminReferral | null>(null);
  const [referralToReject, setReferralToReject] = useState<AdminReferral | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    onSearch(query);
  };

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    onFilterStatus(status === 'all' ? '' : status);
  };

  const handleOpenApproveModal = (referral: AdminReferral) => {
    setReferralToApprove(referral);
    setApproveModalOpen(true);
  };

  const handleApprove = async () => {
    if (referralToApprove) {
      await onApprove(referralToApprove.id);
      setApproveModalOpen(false);
      setReferralToApprove(null);
    }
  };

  const handleOpenRejectModal = (referral: AdminReferral) => {
    setReferralToReject(referral);
    setRejectModalOpen(true);
  };

  const handleReject = async () => {
    if (referralToReject && rejectionReason.trim()) {
      await onReject(referralToReject.id, rejectionReason);
      setRejectModalOpen(false);
      setReferralToReject(null);
      setRejectionReason('');
    }
  };

  const columns: AdminTableColumn<AdminReferral>[] = [
    {
      key: 'referrer_email',
      label: 'Referrer',
      render: (referral) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {referral.referrer_name || 'Unknown'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {referral.referrer_email}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'referred_email',
      label: 'Referred',
      render: (referral) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {referral.referred_name || 'Unknown'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {referral.referred_email}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: 120,
      render: (referral) => (
        <Chip
          label={referral.status}
          color={
            referral.status === 'approved'
              ? 'success'
              : referral.status === 'rejected'
              ? 'error'
              : 'warning'
          }
          size="small"
        />
      ),
    },
    {
      key: 'submission_date',
      label: 'Submitted',
      width: 150,
      render: (referral) => (
        <Typography variant="body2">
          {format(new Date(referral.submission_date), 'MMM d, yyyy')}
        </Typography>
      ),
    },
    {
      key: 'approval_date',
      label: 'Decision Date',
      width: 150,
      render: (referral) => (
        <Typography variant="body2">
          {referral.approval_date
            ? format(new Date(referral.approval_date), 'MMM d, yyyy')
            : referral.rejection_date
            ? format(new Date(referral.rejection_date), 'MMM d, yyyy')
            : '-'}
        </Typography>
      ),
    },
    {
      key: 'rejection_reason',
      label: 'Notes',
      render: (referral) => (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 200,
            display: 'block',
          }}
        >
          {referral.rejection_reason || referral.notes || '-'}
        </Typography>
      ),
    },
  ];

  const actions: AdminTableAction<AdminReferral>[] = [
    {
      label: 'Approve',
      icon: <CheckCircleIcon />,
      onClick: handleOpenApproveModal,
      color: 'success',
      disabled: (referral) => referral.status !== 'pending',
    },
    {
      label: 'Reject',
      icon: <CancelIcon />,
      onClick: handleOpenRejectModal,
      color: 'error',
      disabled: (referral) => referral.status !== 'pending',
    },
  ];

  return (
    <Box>
      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            placeholder="Search referrer or referred email..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => handleStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography variant="body2" color="text.secondary">
              {referrals.length} referrals
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Table */}
      <AdminTable
        columns={columns}
        data={referrals}
        actions={actions}
        isLoading={isLoading}
      />

      {/* Approve Confirmation Modal */}
      <AdminConfirmModal
        open={approveModalOpen}
        title="Approve Referral"
        message={
          referralToApprove
            ? `Approve referral from ${referralToApprove.referrer_email} for ${referralToApprove.referred_email}?`
            : ''
        }
        confirmText="Approve"
        severity="info"
        consequences={[
          'Referrer will receive credit for this referral',
          'Referred user account will be verified',
          'Approval notification will be sent',
        ]}
        onConfirm={handleApprove}
        onCancel={() => {
          setApproveModalOpen(false);
          setReferralToApprove(null);
        }}
      />

      {/* Reject Modal with Reason */}
      <Dialog
        open={rejectModalOpen}
        onClose={() => {
          setRejectModalOpen(false);
          setReferralToReject(null);
          setRejectionReason('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Referral</DialogTitle>
        <DialogContent>
          {referralToReject && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Rejecting referral from <strong>{referralToReject.referrer_email}</strong> for{' '}
                <strong>{referralToReject.referred_email}</strong>
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Explain why this referral is being rejected..."
            required
            helperText="This reason will be recorded in the system"
          />

          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Common reasons:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              {[
                'Email already registered',
                'Invalid email address',
                'Duplicate referral',
                'Suspected fraud',
              ].map((reason) => (
                <Chip
                  key={reason}
                  label={reason}
                  size="small"
                  onClick={() => setRejectionReason(reason)}
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRejectModalOpen(false);
              setReferralToReject(null);
              setRejectionReason('');
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            variant="contained"
            color="error"
            disabled={!rejectionReason.trim()}
            startIcon={<CancelIcon />}
          >
            Reject Referral
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
